/**
 * FY paid-leave balance helpers (GET employeeLeaveRequests/balance/:id or …/me/balance).
 */

export type LeaveBalanceNumbers = {
  entitlement: number;
  credited: number;
  used: number;
  pending: number;
  available: number;
  remainingCap: number;
};

export type LeaveBalanceDisabled = {
  enabled: false;
  financialYear?: string;
};

export type LeaveBalanceEnabled = LeaveBalanceNumbers & {
  enabled: true;
  financialYear?: string;
  employeeId?: number;
};

export type LeaveBalanceResult = LeaveBalanceDisabled | LeaveBalanceEnabled;

export type LeaveBalanceFetchStatus =
  | { status: "loading" }
  | { status: "unavailable"; reason?: string }
  | { status: "disabled"; financialYear?: string }
  | { status: "ready"; balance: LeaveBalanceEnabled };

/** Staff-facing summary derived from API numbers (never leads with a negative). */
export type LeaveBalancePlainSummary = {
  /** Primary sentence for the card. */
  headline: string;
  /** Short supporting lines under the headline. */
  supportingLines: string[];
  /** Compact single-line form of headline + supporting (tests / future compact surfaces). */
  alertText: string;
  /** Alert tone (kept in sync with the minimal visible hint copy). */
  alertSeverity: "info" | "warning";
};

/** Office vs self leave-balance endpoint paths (match Fakhri-api routes). */
export function leaveBalanceApiLocation(
  mode: "office" | "self" = "office",
  employeeId?: number
): string {
  if (mode === "self") {
    return "employeeLeaveRequests/me/balance";
  }
  if (employeeId == null || !Number.isFinite(employeeId)) {
    return "employeeLeaveRequests/balance";
  }
  return `employeeLeaveRequests/balance/${employeeId}`;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Parse Nest leave-balance payloads (enabled or disabled).
 * Returns null when the body is not a recognizable balance shape.
 */
export function parseLeaveBalancePayload(data: unknown): LeaveBalanceResult | null {
  if (!data || typeof data !== "object") return null;
  const body = data as Record<string, unknown>;

  // Some APIs wrap in { data: … } or { rows: [ … ] }
  if (body.data && typeof body.data === "object" && !Array.isArray(body.data)) {
    return parseLeaveBalancePayload(body.data);
  }
  if (Array.isArray(body.rows) && body.rows[0]) {
    return parseLeaveBalancePayload(body.rows[0]);
  }

  if (body.enabled === false) {
    return {
      enabled: false,
      financialYear: typeof body.financialYear === "string" ? body.financialYear : undefined,
    };
  }

  if (body.enabled !== true) return null;

  const entitlement = body.entitlement;
  const credited = body.credited;
  const used = body.used;
  const pending = body.pending;
  const available = body.available;
  const remainingCap = body.remainingCap;
  if (
    !isFiniteNumber(entitlement) ||
    !isFiniteNumber(credited) ||
    !isFiniteNumber(used) ||
    !isFiniteNumber(pending) ||
    !isFiniteNumber(available) ||
    !isFiniteNumber(remainingCap)
  ) {
    return null;
  }

  return {
    enabled: true,
    entitlement,
    credited,
    used,
    pending,
    available,
    remainingCap,
    financialYear: typeof body.financialYear === "string" ? body.financialYear : undefined,
    employeeId: isFiniteNumber(body.employeeId) ? body.employeeId : undefined,
  };
}

/** True when HTTP status means the balance endpoint is not available yet. */
export function isLeaveBalanceEndpointMissing(err: unknown): boolean {
  const status = (err as { response?: { status?: number } })?.response?.status;
  return status === 404 || status === 501 || status === 405;
}

export function formatLeaveBalanceDays(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  const rounded = Math.round(value * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
}

function dayUnit(value: number): string {
  return Math.abs(value) === 1 ? "day" : "days";
}

/** `"3 days"` / `"1 day"` for staff-facing copy. */
export function formatLeaveBalanceDaysWithUnit(value: number): string {
  const formatted = formatLeaveBalanceDays(value);
  if (formatted === "—") return formatted;
  return `${formatted} ${dayUnit(value)}`;
}

/**
 * Plain-language summary for staff who should not need entitlement/cap jargon.
 * Leads with yearly remaining; hints stay minimal (pending + yearly allowance).
 */
export function deriveLeaveBalancePlainSummary(
  balance: LeaveBalanceEnabled
): LeaveBalancePlainSummary {
  const leftThisYear = Math.max(0, round2(balance.remainingCap));

  const headline =
    leftThisYear > 0
      ? `Up to ${formatLeaveBalanceDaysWithUnit(leftThisYear)} of paid leave left this year`
      : "No paid leave left this year";

  const hintParts: string[] = [];
  if (balance.pending > 0) {
    hintParts.push(`Waiting for approval: ${formatLeaveBalanceDaysWithUnit(balance.pending)}`);
  }
  hintParts.push(`Yearly allowance: ${formatLeaveBalanceDaysWithUnit(balance.entitlement)}`);
  const supportingLines = [hintParts.join(" · ")];

  return {
    headline,
    supportingLines,
    alertText: [headline, ...supportingLines].join(" · "),
    // Hints omit advance; keep alert calm so severity matches visible copy.
    alertSeverity: "info",
  };
}

/** Key for remounting/refetching leave-balance UI after review actions. */
export function leaveBalanceUiRefreshKey(parts: {
  status?: string | null;
  updatedAt?: string | null;
}): string {
  return `${parts.status ?? ""}:${parts.updatedAt ?? ""}`;
}

/** Detail grid labels (collapsed “Show details”). */
export const LEAVE_BALANCE_FIELD_LABELS: Array<{
  key: keyof LeaveBalanceNumbers;
  label: string;
}> = [
  { key: "entitlement", label: "Yearly allowance" },
  { key: "credited", label: "Earned so far" },
  { key: "used", label: "Taken (approved)" },
  { key: "pending", label: "Waiting approval" },
  { key: "available", label: "Earned & free now" },
  { key: "remainingCap", label: "Left this year" },
];

/** Display value for a detail field (never show negative “can take now”). */
export function formatLeaveBalanceDetailValue(
  key: keyof LeaveBalanceNumbers,
  balance: LeaveBalanceEnabled
): string {
  if (key === "available") {
    return formatLeaveBalanceDays(Math.max(0, balance.available));
  }
  return formatLeaveBalanceDays(balance[key]);
}

export type LeaveRangePreviewEnabled = {
  enabled: true;
  workingDays: number;
  paidWorkingDays: number;
  unpaidWorkingDays: number;
  remainingCap: number;
  isPaidType: boolean;
  enforceAnnualCap: boolean;
  financialYear?: string;
};

export type LeaveRangePreviewResult =
  | { enabled: false; financialYear?: string }
  | LeaveRangePreviewEnabled;

/** Parse GET …/balance/…/preview payloads. */
export function parseLeaveRangePreviewPayload(data: unknown): LeaveRangePreviewResult | null {
  if (!data || typeof data !== "object") return null;
  const body = data as Record<string, unknown>;
  if (body.data && typeof body.data === "object" && !Array.isArray(body.data)) {
    return parseLeaveRangePreviewPayload(body.data);
  }
  if (body.enabled === false) {
    return {
      enabled: false,
      financialYear: typeof body.financialYear === "string" ? body.financialYear : undefined,
    };
  }
  if (body.enabled !== true) return null;
  const workingDays = body.workingDays;
  const paidWorkingDays = body.paidWorkingDays;
  const unpaidWorkingDays = body.unpaidWorkingDays;
  const remainingCap = body.remainingCap;
  if (
    !isFiniteNumber(workingDays) ||
    !isFiniteNumber(paidWorkingDays) ||
    !isFiniteNumber(unpaidWorkingDays) ||
    !isFiniteNumber(remainingCap)
  ) {
    return null;
  }
  return {
    enabled: true,
    workingDays,
    paidWorkingDays,
    unpaidWorkingDays,
    remainingCap,
    isPaidType: body.isPaidType === true,
    enforceAnnualCap: body.enforceAnnualCap === true,
    financialYear: typeof body.financialYear === "string" ? body.financialYear : undefined,
  };
}

/** Staff-facing lines for a date-range paid/unpaid preview. */
export function formatLeaveRangeBreakupLines(preview: LeaveRangePreviewEnabled): {
  headline: string;
  detail: string;
} {
  const total = formatLeaveBalanceDaysWithUnit(preview.workingDays);
  if (preview.workingDays <= 0) {
    return {
      headline: "No working days in this date range",
      detail: "Weekends and office holidays are not counted.",
    };
  }
  if (!preview.isPaidType) {
    return {
      headline: `${total} (unpaid)`,
      detail: "This leave type does not use paid leave allowance.",
    };
  }
  if (preview.unpaidWorkingDays <= 0) {
    return {
      headline: `${total} · all paid`,
      detail: `Within this year's remaining allowance (${formatLeaveBalanceDaysWithUnit(preview.remainingCap)} left before this request).`,
    };
  }
  if (preview.paidWorkingDays <= 0) {
    return {
      headline: `${total} · all unpaid`,
      detail:
        "Yearly paid allowance is already used or waiting approval. On approval this becomes unpaid leave.",
    };
  }
  return {
    headline: `${total} · Paid ${formatLeaveBalanceDays(preview.paidWorkingDays)} · Unpaid ${formatLeaveBalanceDays(preview.unpaidWorkingDays)}`,
    detail:
      "Days over the yearly allowance become unpaid on approval (request stays one submission until then).",
  };
}

export function leaveRangePreviewApiLocation(mode: "office" | "self", employeeId?: number): string {
  if (mode === "self") {
    return "employeeLeaveRequests/me/balance/preview";
  }
  if (employeeId == null || !Number.isFinite(employeeId)) {
    throw new Error("employeeId is required for office leave range preview");
  }
  return `employeeLeaveRequests/balance/${employeeId}/preview`;
}

export type LeaveRangePreviewQuery = {
  startDate: string;
  endDate: string;
  leaveType: string;
};
